/**
 * Admin module TypeScript types
 */

// ============================================================================
// Stats Types
// ============================================================================

export interface EntityCounts {
  states: number
  government_districts: number
  counties: number
  municipal_associations: number
  municipalities: number
}

export interface ClassificationCounts {
  region_types: number
  county_types: number
  spatial_planning_regions: number  // ROR - Raumordnungsregionen
  labor_market_regions: number  // AMR - Arbeitsmarktregionen
  travel_regions: number
  municipality_type_classifications: number
  urban_rural_types: number
  centrality_types: number
}

export interface LastImportInfo {
  import_date: string | null
  reference_year: number | null
  reference_date_display: string | null
  dataset_name: string | null
  total_records?: number | null
}

export interface StatsOverview {
  entity_counts: EntityCounts
  classification_counts: ClassificationCounts
  last_import: LastImportInfo | null
  database_status: 'ok' | 'empty' | 'outdated'
}

// ============================================================================
// Import Types
// ============================================================================

export interface GV100Preview {
  reference_year: number
  reference_date: string
  reference_date_display: string
  record_counts: Record<string, number>
  sample_records?: Record<string, Array<{ satzart: string; ags: string; name: string }>>
}

export interface FileUploadResponse {
  upload_id: string
  ad_filename: string | null
  nad_filename: string | null
  preview: GV100Preview
}

export interface ImportProgress {
  current: number
  total: number
  percentage: number
  current_step: string
}

export interface ImportJobStatus {
  job_id: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  started_at?: string
  completed_at?: string
  progress: ImportProgress
  errors: string[]
  result?: Record<string, unknown>
}

export interface ImportResult {
  status: string
  total_records: number
  successful_records: number
  failed_records: number
  skipped_records: number
  errors: string[]
  warnings: string[]
  reference_year: number
  edition_name?: string
  edition_mode?: string
}

// ============================================================================
// Edition Types
// ============================================================================

export interface EditionInfo {
  id: number
  provider: string
  dataset_name: string
  edition: string
  reference_year: number
  reference_date_display: string
  import_date: string | null
  is_latest: boolean
  records_imported: number | null
  includes_nad: boolean
}

export interface EditionsListResponse {
  editions: EditionInfo[]
  datasets: string[]
}

export type EditionMode = 'update_existing' | 'create_new'

export interface ImportExecuteParams {
  mode: 'upsert' | 'replace'
  edition_mode: EditionMode
  edition_name?: string
  target_edition_id?: number
}

// ============================================================================
// Entity List Types
// ============================================================================

export interface EntityListResponse<T = Record<string, unknown>> {
  total: number
  limit: number
  offset: number
  results: T[]
}

export interface StateListItem {
  id: number
  code: string
  name: string
  designation: string | null
  capital: string | null
  population: number | null
  area_km2: number | null
  county_count: number
}

export interface GovernmentDistrictListItem {
  id: number
  ags: string
  name: string
  designation: string | null
  state_name: string | null
  administrative_seat: string | null
}

export interface CountyListItem {
  id: number
  ags: string
  name: string
  designation: string | null
  textkennzeichen: string | null
  textkennzeichen_label: string | null
  state_name: string | null
  population: number | null
  municipality_count: number
}

export interface MunicipalAssociationListItem {
  id: number
  ars: string
  name: string
  designation: string | null
  textkennzeichen: string | null
  county_name: string | null
  population: number | null
  municipality_count: number
}

export interface MunicipalityListItem {
  id: number
  ags: string
  ars: string
  name: string
  designation: string | null
  name_addon: string | null
  textkennzeichen: string | null
  county_name: string | null
  population: number | null
  area_km2: number | null
}

export type EntityType =
  | 'states'
  | 'government-districts'
  | 'counties'
  | 'municipal-associations'
  | 'municipalities'

export const ENTITY_LABELS: Record<EntityType, string> = {
  'states': 'Bundesländer',
  'government-districts': 'Regierungsbezirke',
  'counties': 'Kreise',
  'municipal-associations': 'Gemeindeverbände',
  'municipalities': 'Gemeinden',
}

// ============================================================================
// Classification Types (NAD)
// ============================================================================

export type ClassificationType =
  | 'region-types'
  | 'county-types'
  | 'spatial-planning-regions'
  | 'labor-market-regions'
  | 'travel-regions'
  | 'urbanization-areas'
  | 'municipality-types'
  | 'urban-rural-types'
  | 'centrality-types'
  | 'text-indicators'
  | 'prognos-rating-classes'
  // BBSR Statistical Regions (INKAR)
  | 'metropolitan-regions'
  | 'major-city-regions'
  | 'ihk-districts'
  | 'bbsr-middle-areas'
  | 'county-regions'

export const CLASSIFICATION_LABELS: Record<ClassificationType, string> = {
  'region-types': 'Regionstypen',
  'county-types': 'Kreistypen',
  'spatial-planning-regions': 'Raumordnungsregionen',
  'labor-market-regions': 'Arbeitsmarktregionen',
  'travel-regions': 'Reisegebiete',
  'urbanization-areas': 'Verdichtungsräume (MKRO)',
  'municipality-types': 'Gemeindetypen',
  'urban-rural-types': 'Stadt-Land-Typen',
  'centrality-types': 'Zentralitätstypen',
  'text-indicators': 'Textkennzeichen',
  'prognos-rating-classes': 'Prognos Klassen',
  // BBSR Statistical Regions (INKAR)
  'metropolitan-regions': 'Metropolregionen',
  'major-city-regions': 'Großstadtregionen',
  'ihk-districts': 'IHK-Bezirke',
  'bbsr-middle-areas': 'Mittelbereiche',
  'county-regions': 'Kreisregionen',
}

export interface ClassificationListItem {
  id: number
  code: string
  bbsr_code?: string | null
  category?: string | null  // For TextIndicators: "40", "50", or "60"
  name: string
  description?: string | null
  indicator_count?: number  // Number of INKAR indicators for this statistical entity
}

// ============================================================================
// CSV Import Types
// ============================================================================

export type CSVImportStep = 'upload' | 'preview' | 'mapping' | 'progress' | 'complete'

export type EntityLevel =
  | 'federal'
  | 'state'
  | 'government_district'
  | 'county'
  | 'municipal_association'
  | 'municipality'

export type KeyType = 'ags_2' | 'ags_5' | 'ags_8' | 'ars_12' | 'name'

export type TargetTable = 'indicator_values'

export const ENTITY_LEVEL_LABELS: Record<EntityLevel, string> = {
  federal: 'Bund',
  state: 'Bundesland',
  government_district: 'Regierungsbezirk',
  county: 'Kreis',
  municipal_association: 'Gemeindeverband',
  municipality: 'Gemeinde',
}

export const TARGET_TABLE_LABELS: Record<TargetTable, string> = {
  indicator_values: 'Indikatoren',
}

export interface CSVColumnInfo {
  name: string
  sample_values: string[]
  detected_type: 'integer' | 'decimal' | 'text'
  suggested_mapping: string | null
}

export interface ColumnMappingConfig {
  csv_column: string
  target: string
  data_type: 'integer' | 'decimal' | 'text'
  transform?: string | null
  skip: boolean
}

export interface CSVUploadResponse {
  upload_id: string
  filename: string
  detected_delimiter: string
  detected_encoding: string
  columns: CSVColumnInfo[]
  sample_rows: Record<string, unknown>[]
  row_count: number
  suggested_template: ImportTemplateInfo | null
  suggested_key_column: string | null
  suggested_key_type: KeyType | null
  detected_entity_level: EntityLevel | null
}

export interface ImportTemplateInfo {
  id: number
  name: string
  description: string | null
  provider: string
  entity_level: EntityLevel
  target_table: TargetTable
  delimiter: string
  encoding: string
  is_system: boolean
  last_used_at: string | null
  use_count: number
}

export interface ImportTemplateDetail extends ImportTemplateInfo {
  skip_rows: number
  key_column: string
  key_type: KeyType
  column_mappings: Record<string, ColumnMappingConfig>
  created_by: string | null
  created_at: string | null
}

export interface ImportTemplateCreate {
  name: string
  description?: string
  provider: string
  entity_level: EntityLevel
  target_table: TargetTable
  delimiter: string
  encoding: string
  skip_rows?: number
  key_column: string
  key_type: KeyType
  column_mappings: Record<string, ColumnMappingConfig>
}

export interface CSVImportExecuteParams {
  column_mappings: ColumnMappingConfig[]  // Array with csv_column included
  target_table: TargetTable
  entity_level: EntityLevel
  key_column: string
  key_type: KeyType
  reference_year: number
  delimiter?: string
  encoding?: string
  edition_mode: EditionMode
  edition_name?: string
  target_edition_id?: number
  template_id?: number
  save_as_template?: string
  template_provider?: string
}

// Imported CSV datasets (Admin > CSV Importe > Importierte Daten)
export interface ImportedIndicatorInfo {
  id: number
  code: string
  name: string
  unit: string | null
  category: string | null
  is_visible: boolean
  value_count: number
  years: number[]
}

export interface ImportedDatasetInfo {
  id: number
  provider: string
  dataset_name: string
  edition: string | null
  parent_edition: string | null
  import_date: string | null
  import_file: string | null
  records_imported: number | null
  entity_types: string[]
  years: number[]
  value_count: number
  indicators: ImportedIndicatorInfo[]
  template_id: number | null
}

export interface ImportedValueRow {
  entity_id: number
  entity_key: string | null
  entity_name: string | null
  entity_type: string
  reference_year: number
  value: string | null
}

export interface ImportedValuesResponse {
  data_source_id: number
  indicator_id: number
  indicator_code: string
  indicator_name: string
  reference_year: number | null
  total: number
  rows: ImportedValueRow[]
}

export interface CSVImportResponse {
  job_id: string
  status: 'queued' | 'started' | 'completed' | 'failed'
  message: string
  background: boolean
  total_records?: number
  successful_records?: number
  failed_records?: number
  skipped_records?: number
  errors?: string[]
  warnings?: string[]
  result?: CSVImportResult
}

export interface CSVImportResult {
  status: 'completed' | 'partial' | 'failed'
  total_records: number
  successful_records: number
  failed_records: number
  skipped_records: number
  duration_seconds?: number
  success_rate?: number
  errors: string[]
  warnings?: string[]
  edition_id?: number
  edition_name?: string
  edition_mode?: EditionMode
  reference_year: number
  target_table: string
}

export interface ExistingDataCheckResponse {
  exists: boolean
  count: number
  table: string
  reference_year: number
  message: string
}

// ============================================================================
// Data Sources Types
// ============================================================================

export interface DataSourceInfo {
  table_name: string
  display_name: string
  description: string | null
  provider: string
  entity_levels: EntityLevel[]
  record_count: number
  years_available: number[]
  last_updated: string | null
}

export interface DataSourceField {
  id: number
  field_name: string
  display_label: string
  default_label: string
  custom_label: string | null
  data_type: 'integer' | 'decimal' | 'text'
  description: string | null
  display_order: number
  is_visible: boolean
}

export interface DataSourceDetailResponse {
  table_name: string
  display_name: string
  description: string | null
  provider: string
  entity_levels: EntityLevel[]
  fields: DataSourceField[]
  stats: Record<string, unknown>
}

export interface DataSourcesListResponse {
  sources: DataSourceInfo[]
  entity_level: EntityLevel | null
}

export interface FieldLabelUpdateRequest {
  custom_label?: string | null
  description?: string | null
  display_order?: number
  is_visible?: boolean
}

export interface FieldLabelResponse {
  id: number
  source_table: string
  field_name: string
  default_label: string
  custom_label: string | null
  display_label: string
  description: string | null
  display_order: number
  is_visible: boolean
}

// ============================================================================
// Report Layouts (Phase B)
// ============================================================================

export type RowType = 'comparison' | 'single' | 'composite' | 'flag' | 'list' | 'prose'
export type Visibility = 'web' | 'prompt' | 'both'

export interface LayoutRow {
  label: string
  source?: string
  row_type: RowType
  decimals?: number
  columns?: string[]
  visibility?: Visibility
  hide_if_empty?: boolean
  options?: Record<string, unknown>
}
export interface LayoutLeg { label: string | null; rating_key?: string | null; zeilen: LayoutRow[]; [k: string]: unknown }
export interface LayoutSubgroup { label: string | null; rating_key?: string | null; zeilen?: LayoutRow[]; legs?: LayoutLeg[]; [k: string]: unknown }
export interface LayoutBereich { titel: string; rating_key?: string | null; subgruppen: LayoutSubgroup[] }
export interface LayoutGroup { ebene: 'makro' | 'mikro'; titel_template: string; rating_key?: string | null; bereiche: LayoutBereich[] }
export interface LayoutConfig { groups: LayoutGroup[] }
export interface ReportLayout { key: string; name: string; description: string | null; is_active: boolean; version: number; config: LayoutConfig }

export interface SourceMeta { kind: string; label: string; row_types: RowType[]; levels: ('makro' | 'mikro')[] }
export interface LayoutCatalog { sources: SourceMeta[]; row_types: RowType[]; rating_keys: string[] }
export interface LayoutPreview { table: unknown; markdown_web: string; markdown_prompt: string }

// ============================================================================
// Rating Config (Phase D2)
// ============================================================================

export interface RatingComponent { ref: string; weight: number }
export interface RatingNode {
  key: string
  type: 'quantile' | 'weighted' | 'prognos_note' | 'ordinal_display'
  match?: 'name' | 'code' | 'calculated'
  indicator?: string
  higher_is_better?: boolean
  rank_field?: string
  scale?: string
  components?: RatingComponent[]
  [k: string]: unknown
}
export interface RatingLevel { entity_level: 'county' | 'municipality'; root: string; nodes: RatingNode[] }
export interface RatingConfigTree {
  makro: RatingLevel; mikro: RatingLevel
  scales: Record<string, [number, string][]>
  prognos: { bins: [number, string][]; note_cum_shares: number[] }
  [k: string]: unknown
}
export interface RatingConfig { key: string; name: string; description: string | null; is_active: boolean; version: number; config: RatingConfigTree }
export interface RatingCatalog { indicators: { name: string; code: string | null; source: string | null }[]; node_keys: string[]; scales: string[]; node_types: string[]; matches: string[] }
export interface RatingPreview { makro: Record<string, { score?: number; term?: string | null; rang?: number; total?: number } | null>; mikro: Record<string, { score?: number; term?: string | null } | null> }

export interface PromptTemplate {
  id: number
  model_name: string
  display_name: string | null
  description: string | null
  prompt_text: string
  is_active: boolean
  sort_order: number | null
  api_model: string | null
  temperature: string | null
  max_tokens: number | null
}
export type PromptCreate = Omit<PromptTemplate, 'id'>
export type PromptUpdate = Partial<Omit<PromptTemplate, 'id' | 'model_name'>>
